import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getTutuUrl } from "../utils/navigationUtils";
import { UIUtils } from "../utils/uiUtils";
import mobileLinkIcon from "../assets/icons/mobile-link-icon.svg";
import forwardArrowIcon from "../assets/icons/forward-arrow-icon.svg";

interface CreditOption {
  label: string;
  subLabel?: string;
  iconRotation: string;
  trackingLabel: string;
  action: (navigate: ReturnType<typeof useNavigate>) => void;
}

export const CREDIT_OPTIONS: CreditOption[] = [
  {
    label: "Loans under £1,000",
    iconRotation: "rotate(1.47deg)",
    trackingLabel: "Nav credit loans under 1000",
    action: (navigate) => navigate("/loans-landing"),
  },
  {
    label: "Loans £1,000 to £10,000",
    subLabel: "(Takes you to Tutu.co.uk)",
    iconRotation: "rotate(100.72deg)",
    trackingLabel: "Nav credit loans 1000 to 10000",
    action: () =>
      window.open(getTutuUrl("nav_link"), "_blank", "noopener,noreferrer"),
  },
];

interface CreditOptionsListProps {
  /** Called after an option has been actioned, e.g. to close the menu */
  onSelect?: () => void;
  /** Vertical spacing between the options */
  gapClassName?: string;
}

/**
 * The two credit options (sub-£1,000 loans via Afforda, £1,000–£10,000
 * guarantor loans via Tutu). Shared by the mobile overlay and the desktop
 * dropdown so the content can't drift between the two.
 */
export const CreditOptionsList: React.FC<CreditOptionsListProps> = ({
  onSelect,
  gapClassName = "gap-9",
}) => {
  const navigate = useNavigate();

  return (
    <ul className={`flex flex-col ${gapClassName} w-full`}>
      {CREDIT_OPTIONS.map((option) => (
        <li key={option.label}>
          <button
            type="button"
            onClick={() => {
              UIUtils.trackButtonClick(option.trackingLabel);
              option.action(navigate);
              onSelect?.();
            }}
            className="flex flex-row items-center w-full text-left text-black"
          >
            <div className="w-[46px] flex justify-center items-center flex-shrink-0">
              <img
                src={mobileLinkIcon}
                alt=""
                className="w-[30.59px] h-[26.48px]"
                style={{ transform: option.iconRotation }}
              />
            </div>
            <span className="flex flex-col flex-1 min-w-0">
              <span className="font-pp-pangram-compressed font-extrabold text-[28px] leading-[90%]">
                {option.label}
              </span>
              {option.subLabel && (
                <span className="text-sm font-semibold leading-[135%]">
                  {option.subLabel}
                </span>
              )}
            </span>
            <img
              src={forwardArrowIcon}
              alt=""
              className="w-6 h-6 flex-shrink-0 ml-4"
            />
          </button>
        </li>
      ))}
    </ul>
  );
};

interface CreditOptionsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Mobile credit navigation window. Dims the page and shows the credit
 * options in a blue panel between the header and the bottom nav bar.
 */
export const CreditOptionsOverlay: React.FC<CreditOptionsOverlayProps> = ({
  isOpen,
  onClose,
}) => {
  // Lock page scroll while the window is open, like the mobile menu does
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="sm:hidden fixed inset-0 z-50"
      role="dialog"
      aria-label="Credit options"
      data-testid="credit-options-overlay"
    >
      <div
        className="fixed inset-0 bg-black bg-opacity-50"
        onClick={onClose}
        role="button"
        aria-label="Close credit options"
      />
      <div className="absolute left-4 right-4 top-[76px] bottom-[95px] flex flex-col justify-center px-[34px] bg-primary rounded-30 overflow-y-auto">
        <CreditOptionsList onSelect={onClose} />
      </div>
    </div>
  );
};
